import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../Header/Header';
import LazyImage from '../LazyImage';
import { LandingPageContext } from '../../contexts/LandingPageContext';
import { getLocalizedText } from '../../utils/localized';
import './EventsPage.css';

const EventsPage = () => {
    const { t, i18n } = useTranslation();
    const { eventsSection } = useContext(LandingPageContext);
    const [imageExists, setImageExists] = useState(true);
    const lang = i18n.language || 'de';
    const events = Array.isArray(eventsSection?.events) ? eventsSection.events : [];
    const hasEvents = events.length > 0;
    const heroImage =
        (hasEvents && events.find((ev) => ev.image)?.image) ||
        (eventsSection?.image) ||
        '';
    const croppedHero = heroImage ? heroImage : null;

    useEffect(() => {
        try {
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'manual';
            }
        } catch (e) {
            // ignore
        }

        const computeHeaderOffset = () => {
            try {
                const root = document.documentElement;
                const cssVal = getComputedStyle(root).getPropertyValue('--header-offset');
                if (cssVal) {
                    const parsed = parseInt(cssVal.trim(), 10);
                    if (!isNaN(parsed)) return parsed;
                }
            } catch (e) {
                // ignore
            }

            try {
                const headerEl = document.querySelector('.header');
                if (headerEl) {
                    const rect = headerEl.getBoundingClientRect();
                    const top = Math.max(0, Math.round(rect.top));
                    const height = Math.round(rect.height || 0);
                    return top + height + 8;
                }
            } catch (e) { /* ignore */ }

            return 28; // fallback
        };

        let attempts = 0;
        const maxAttempts = 40; // try for up to ~4s
        const interval = 100;
        const timer = setInterval(() => {
            attempts += 1;
            try {
                const offset = computeHeaderOffset();
                const titleEl = document.querySelector('.events-title');
                const pageEl = document.querySelector('.events-page');
                const el = titleEl || pageEl;
                if (el) {
                    const rect = el.getBoundingClientRect();
                    const targetY = rect.top + (window.scrollY || window.pageYOffset || 0) - offset;
                    const curY = window.scrollY || window.pageYOffset || 0;
                    if (Math.abs(curY - targetY) <= 3) {
                        clearInterval(timer);
                        return;
                    }

                    window.scrollTo({ top: Math.max(0, Math.floor(targetY)), behavior: 'auto' });
                    const newY = window.scrollY || window.pageYOffset || 0;
                    if (Math.abs(newY - targetY) <= 3 || attempts >= maxAttempts) {
                        clearInterval(timer);
                        return;
                    }
                }

                if (attempts >= maxAttempts) {
                    clearInterval(timer);
                }
            } catch (err) {
                if (attempts >= maxAttempts) clearInterval(timer);
            }
        }, interval);

        return () => {
            try { clearInterval(timer); } catch (e) { /* ignore */ }
        };
    }, []);

    return (
        <div className="events-page-root">
            <Header />
            <main className="events-page">
                <div className="events-container">
                    <h1 className="events-title">
                        {getLocalizedText(eventsSection?.headerTitle, lang) || t('events.title')}
                    </h1>
                    <p className="events-subtitle">
                        {getLocalizedText(eventsSection?.headerSubtitle, lang) || t('events.subtitle')}
                    </p>

                    {croppedHero && (
                        <div className="events-hero">
                            {imageExists ? (
                                <LazyImage
                                    src={croppedHero}
                                    alt={t('events.title')}
                                    className="events-hero-img"
                                    onError={() => setImageExists(false)}
                                    onLoad={() => setImageExists(true)}
                                />
                            ) : (
                                <div className="events-hero-fallback"></div>
                            )}
                        </div>
                    )}

                    <section className="events-list">
                        {hasEvents ? (
                            events.map((ev, idx) => {
                                const title = getLocalizedText(ev.title, lang);
                                const description = getLocalizedText(ev.description, lang);
                                return (
                                    <article key={ev.id || idx} className="event-card">
                                        {ev.image && (
                                            <div className="event-card-media">
                                                <LazyImage src={ev.image} alt={title || t('events.title')} className="event-card-img" />
                                            </div>
                                        )}
                                        <div className="event-card-body">
                                            {ev.date && <time className="event-card-date">{ev.date}</time>}
                                            {title && <h3 className="event-card-title">{title}</h3>}
                                            {description && <p className="event-card-desc">{description}</p>}
                                        </div>
                                    </article>
                                );
                            })
                        ) : (
                            <div className="events-placeholder">
                                {t('events.placeholder', { defaultValue: 'No events are configured yet. Please check back later.' })}
                            </div>
                        )}

                        <div className="events-actions">
                            <Link to="/contact" className="events-request-button">
                                Anfrage / Anmeldung
                            </Link>
                            <Link to="/" className="events-go-home-button">
                                {t('events.goHome', { defaultValue: 'zum TAFELBERG' })}
                            </Link>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default EventsPage;
